import { Link } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import { ArrowUpRight, Check } from "lucide-react";
import { SectionHead, Reveal, GlowCard, Eyebrow } from "../site/Kit";
import { Icon } from "../site/Icon";
import { FM_PAGES, PROMISE } from "../../content/fm";

const pad = (i) => String(i + 1).padStart(2, "0");

export const Steps = ({ eyebrow, title, intro, items, testid = "fm-steps" }) => (
  <section className="container-x py-20 sm:py-28" data-testid={testid}>
    <SectionHead eyebrow={eyebrow} title={title} intro={intro} />
    <ol className={`relative mt-14 grid gap-4 sm:grid-cols-2 ${items.length === 4 ? "lg:grid-cols-4" : items.length === 5 ? "lg:grid-cols-5" : "lg:grid-cols-3 xl:grid-cols-6"}`}>
      <span aria-hidden="true" className="absolute left-0 right-0 top-[27px] hidden h-px bg-gradient-to-r from-gold/0 via-gold/40 to-gold/0 xl:block" />
      {items.map((s, i) => (
        <Reveal as="li" key={s.title} delay={i * 60} className="relative rounded-2xl border hairline bg-navy/60 p-6" data-testid={`${testid}-item-${i + 1}`}>
          <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border border-gold/50 bg-navy font-mono text-[11px] text-gold">{pad(i)}</span>
          <h3 className="mt-5 text-lg font-semibold tracking-tight">{s.title}</h3>
          <p className="mt-2 text-[14.5px] leading-relaxed text-mist">{s.copy}</p>
        </Reveal>
      ))}
    </ol>
  </section>
);

export const Groups = ({ eyebrow, title, intro, groups, numbered, testid = "fm-groups" }) => (
  <section className="border-y hairline bg-deep/40" data-testid={testid}>
    <div className="container-x py-20 sm:py-28">
      <SectionHead eyebrow={eyebrow} title={title} intro={intro} />
      <div className={`mt-14 grid gap-4 sm:grid-cols-2 ${groups.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {groups.map((g, i) => (
          <GlowCard key={g.title} delay={(i % 3) * 70} className="p-7 sm:p-8" data-testid={`${testid}-card-${i + 1}`}>
            <div className="flex items-center justify-between">
              <Icon name={g.icon} className="h-7 w-7 text-gold" />
              {numbered && <span className="font-mono text-xs text-mist">{pad(i)}</span>}
            </div>
            <h3 className="mt-6 text-xl font-semibold tracking-tight">{g.title}</h3>
            {g.copy && <p className="mt-3 text-[15px] leading-relaxed text-mist">{g.copy}</p>}
            {g.items && (
              <ul className="mt-5 space-y-2.5 border-t hairline pt-5">
                {g.items.map((x) => (
                  <li key={x} className="flex items-start gap-2.5 text-[14.5px] text-paper/85">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold" aria-hidden="true" />{x}
                  </li>
                ))}
              </ul>
            )}
          </GlowCard>
        ))}
      </div>
    </div>
  </section>
);

export const TabbedLists = ({ eyebrow, title, intro, groups, testid = "fm-tabs" }) => (
  <section className="container-x py-20 sm:py-28" data-testid={testid}>
    <SectionHead eyebrow={eyebrow} title={title} intro={intro} />
    <Tabs.Root defaultValue="0" className="mt-12">
      <Tabs.List aria-label={title} className="inline-flex flex-wrap gap-2 rounded-full border hairline bg-deep/60 p-1.5">
        {groups.map((g, i) => (
          <Tabs.Trigger
            key={g.title}
            value={String(i)}
            data-testid={`${testid}-trigger-${i + 1}`}
            className="rounded-full px-5 py-2.5 text-sm font-medium text-mist transition-colors hover:text-paper data-[state=active]:bg-gold data-[state=active]:text-navy"
          >
            {g.title}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {groups.map((g, i) => (
        <Tabs.Content key={g.title} value={String(i)} className="mt-8 focus-visible:outline-gold">
          <ul className="grid gap-3 sm:grid-cols-2">
            {g.items.map((x, i) => (
              <li key={x} className="flex items-center gap-4 rounded-xl border hairline bg-navy/50 px-5 py-4 text-[15px] text-paper/90">
                <span className="font-mono text-[11px] text-gold">{pad(i)}</span>{x}
              </li>
            ))}
          </ul>
        </Tabs.Content>
      ))}
    </Tabs.Root>
  </section>
);

export const Split = ({ eyebrow, title, copy, note, testid = "fm-split" }) => (
  <section className="container-x grid gap-10 py-20 sm:py-28 lg:grid-cols-2" data-testid={testid}>
    <SectionHead eyebrow={eyebrow} title={title} />
    <Reveal delay={100} className="space-y-5 text-base md:text-lg leading-relaxed text-mist">
      {copy.map((p, i) =>
        note && i === copy.length - 1 ? (
          <p key={p} className="rounded-2xl border border-gold/25 bg-gold/[0.05] p-6 text-paper" data-testid={`${testid}-note`}>
            <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{note}</span>{p}
          </p>
        ) : <p key={p}>{p}</p>
      )}
    </Reveal>
  </section>
);

export const Callout = ({ eyebrow, title, copy, tone, testid = "fm-callout" }) => (
  <section className="container-x py-12 sm:py-16" data-testid={testid}>
    <Reveal className={`relative overflow-hidden rounded-[28px] border p-8 sm:p-12 ${tone === "alert" ? "border-alert/40 bg-alert/[0.06]" : "border-gold/25 bg-deep"}`}>
      {tone !== "alert" && <div className="gold-glow pointer-events-none absolute -right-24 -top-32 h-[420px] w-[420px]" aria-hidden="true" />}
      <div className="relative max-w-3xl">
        <Eyebrow tone={tone === "alert" ? "alert" : "gold"}>{eyebrow}</Eyebrow>
        <p className="mt-5 font-display text-2xl sm:text-3xl font-semibold leading-tight tracking-[-0.02em]">{title}</p>
        {copy && <p className="mt-4 text-base md:text-lg text-mist">{copy}</p>}
      </div>
    </Reveal>
  </section>
);

export const DataTable = ({ caption, columns, rows, testid = "fm-table" }) => (
  <Reveal className="mt-12 overflow-x-auto rounded-2xl border hairline" data-testid={testid}>
    <table className="w-full min-w-[640px] text-left text-[14.5px]">
      <caption className="sr-only">{caption}</caption>
      <thead className="bg-deep/80 font-mono text-[11px] uppercase tracking-[0.18em] text-gold">
        <tr>{columns.map((c) => <th key={c} scope="col" className="px-5 py-4 font-normal">{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r[0]} className="border-t hairline">
            {r.map((c, i) => i === 0
              ? <th key={c} scope="row" className="px-5 py-4 font-semibold text-paper">{c}</th>
              : <td key={c} className="px-5 py-4 text-mist">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </Reveal>
);

export const CheckList = ({ items, testid }) => (
  <ul className="space-y-3" data-testid={testid}>
    {items.map((x) => (
      <li key={x} className="flex items-start gap-3 text-[15px] text-paper/90">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" />{x}
      </li>
    ))}
  </ul>
);

export const PromiseBand = () => (
  <section className="relative overflow-hidden border-y hairline bg-[#041626]" data-testid="fm-promise">
    <div className="gold-glow pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[820px] -translate-x-1/2 -translate-y-1/2 opacity-70" aria-hidden="true" />
    <div className="container-x relative py-24 text-center sm:py-32">
      <Eyebrow className="justify-center">The NECOS.FM Promise</Eyebrow>
      <Reveal as="p" className="mx-auto mt-8 max-w-4xl font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1] tracking-[-0.03em]">
        {PROMISE.split(". ").map((s, i, a) => (
          <span key={s} className={i === 1 ? "text-gold" : ""}>{s}{i < a.length - 1 ? ". " : ""}</span>
        ))}
      </Reveal>
    </div>
  </section>
);

export const ServiceGrid = ({ exclude, testid = "fm-service-grid" }) => (
  <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid={testid}>
    {FM_PAGES.filter((p) => !exclude?.includes(p.slug)).map((p, i) => (
      <GlowCard
        key={p.slug}
        as={Link}
        to={`/facilities-management/${p.slug}`}
        delay={(i % 5) * 60}
        className="group flex min-h-[220px] flex-col justify-between p-6"
        data-testid={`${testid}-${p.slug}`}
      >
        <div className="flex items-start justify-between">
          <Icon name={p.icon} className="h-7 w-7 text-gold" />
          <ArrowUpRight className="h-4 w-4 text-mist transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-lg font-semibold leading-snug tracking-tight transition-colors group-hover:text-gold">{p.title}</h3>
          <p className="mt-2 text-[14px] leading-relaxed text-mist">{p.desc}</p>
        </div>
      </GlowCard>
    ))}
  </div>
);

export const Related = ({ slugs }) => (
  <section className="container-x py-20 sm:py-24" data-testid="fm-related">
    <SectionHead eyebrow="Connected services" title="Part of one joined-up model." />
    <div className="mt-12 grid gap-4 md:grid-cols-3">
      {slugs.map((s, i) => {
        const p = FM_PAGES.find((x) => x.slug === s);
        return (
          <GlowCard key={s} as={Link} to={`/facilities-management/${s}`} delay={i * 70} className="group flex items-center gap-5 p-6" data-testid={`fm-related-${s}`}>
            <Icon name={p.icon} className="h-6 w-6 shrink-0 text-gold" />
            <div className="min-w-0">
              <p className="font-semibold group-hover:text-gold transition-colors">{p.title}</p>
              <p className="mt-1 text-[14px] text-mist">{p.desc}</p>
            </div>
          </GlowCard>
        );
      })}
    </div>
    <Reveal className="mt-8">
      <Link to="/facilities-management" className="text-[15px] font-medium text-gold link-underline" data-testid="fm-related-overview">All facilities management services</Link>
    </Reveal>
  </section>
);

export const Block = ({ b, i }) => {
  const id = `fm-block-${i + 1}`;
  if (b.type === "steps") return <Steps {...b} testid={id} />;
  if (b.type === "groups") return <Groups {...b} testid={id} />;
  if (b.type === "tabs") return <TabbedLists {...b} testid={id} />;
  if (b.type === "split") return <Split {...b} testid={id} />;
  if (b.type === "callout") return <Callout {...b} testid={id} />;
  return null;
};
